import { createContext, useContext, useEffect, useMemo } from 'react';

interface TooltipWheelContextValue {
  tooltipContentRef: React.RefObject<HTMLDivElement>;
}

const TooltipWheelContext = createContext<TooltipWheelContextValue>({
  tooltipContentRef: { current: null },
});

export const useTooltipWheelContext = () => {
  return useContext(TooltipWheelContext);
};

/**
 * This context is used to prevent scrolling of Window Level when the customTooltip has a scrollable content
 * This would be used in charts, and passed into <CustomTooltip /> since CustomTooltip is a child of Chart Component
 * which not allow to use any hooks.
 */
export const TooltipWheelProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { tooltipContentRef } = useContext(TooltipWheelContext);
  useEffect(() => {
    const onWheelEventListener = (e: WheelEvent) => {
      if (
        tooltipContentRef.current &&
        tooltipContentRef.current.childNodes.length > 0
      ) {
        // if the height of the tooltip content is greater than the height of the window,
        // prevent the wheel event and scroll the tooltip content
        if (
          tooltipContentRef.current.scrollHeight >
          tooltipContentRef.current.clientHeight
        ) {
          e.preventDefault();
          tooltipContentRef.current.scrollTop += e.deltaY;
        }
      }
      return;
    };

    window.addEventListener('wheel', onWheelEventListener, {
      passive: false,
    });

    return () => {
      window.removeEventListener('wheel', onWheelEventListener);
    };
  }, [tooltipContentRef]);

  const memoizedTooltipWheelContext = useMemo(
    () => ({ tooltipContentRef }),
    [tooltipContentRef],
  );

  return (
    <TooltipWheelContext.Provider value={memoizedTooltipWheelContext}>
      {children}
    </TooltipWheelContext.Provider>
  );
};
